export type GetContentResponse = {
    folders: Array<Folder>
    tracks: Array<Track>
    pathToCurrentFolder: { folderName: string; folderID: string }[]
}

export type TracksItem = Folder | Track

export type Folder = {
    _id: string
    name: string
    tracks?: Array<Track>
    folders?: Array<Folder>
    index?: number
    parentID?: string
    onOptionsClick?: ($event) => void
}

export type TrackFolder = {
    _id: string
    userID: string
    name: string
    parentID: string
    createdAt: Date
    deleted: boolean
}

export interface StructuredPrompt {
    text: string
    artist: string | undefined
    genre: string | undefined
    mood: string | undefined
    song_title: string | undefined
}

export type Track = {
    _id: string
    userID: string
    duration: number
    name: string
    index?: number
    style: string
    firstDownload: Date | null
    prompt: StructuredPrompt
    createdAt: Date
    parentID: string
    deleted: boolean
    liked: boolean
    loading: number
    titlePending?: boolean
    audioFormat?: "mp3"
    lyrics?: string
    onOptionsClick?: ($event) => void
}

export interface DownloadTrackParameters {
    trackID: string
    type: "mp3"
}

export interface DownloadFolderParameters {
    folderID: string
    type: "mp3"
}
export interface ContentGetParameters {
    selectedFolderID: string | ""
}

export interface ContentDeleteParameters {
    trackIDsToDelete: string[]
    folderIDsToDelete: string[]
}

export interface RenameContentParameters {
    id: string
    type: "folder" | "track"
    newName: string
}

export interface LikeContentParameters {
    trackID: string
    like: boolean
}

export interface ContentMoveParameters {
    trackIDsToMove: string[]
    folderIDsToMove: string[]
    newParentID: string
}

export interface CreateFolderParameters {
    parentID: string
    name?: string
}

export interface StreamContentParameters {
    trackID: string
    requestTimestamp: number
}

export interface GenerateContentParameters {
    parentID: string | ""
    prompt: string
    duration: number
    audioInfluence?: string
    lyrics?: string
    lyricsMode?: LyricsMode
    vocalGender?: VocalGender
}

export interface GenerateSongParameters {
    prompt: string
    instrumental: boolean
    parentID?: string
    lyrics?: string | null
    vocalGender?: VocalGender | null
    numCompositions: number
}

export interface SongGenerationQuotaResponse {
    dailyGenerationsLeft: number
    dailyGenerationsQuota: number
    hourlyGenerationsLeft: number | null
    hourlyGenerationsQuota: number | null
}

export interface LyricsGenerationQuotaResponse {
    dailyGenerationsLeft: number
    dailyGenerationsQuota: number
    monthlyGenerationsLeft: number
    monthlyGenerationsQuota: number
}

export type SongGenerationKnownEventType =
    | "tracks.created"
    | "track.updated"
    | "generation.accepted"
    | "generation.queued"
    | "generation.started"
    | "composition.started"
    | "composition.decoding_started"
    | "composition.completed"
    | "composition.failed"
    | "composition.cancelled"
    | "audio.start"
    | "audio.chunk"
    | "audio.done"
    | "generation.completed"
    | "generation.error"

/**
 * An NDJSON lifecycle event emitted by the song-generation endpoint. Provider
 * fields remain open so progress metadata can pass through without renaming.
 * Binary payloads are attached to audio.chunk as audio_base64.
 */
export interface SongGenerationStreamEvent {
    type: SongGenerationKnownEventType
    session_id?: string
    duration?: number
    num_compositions?: number
    compositions?: Array<{
        composition_id: string
        index: number
        seed: string
    }>
    requested_permits?: number
    queued_requests?: number
    queue_wait_ms?: number
    granted_permits?: number
    composition_id?: string
    index?: number
    seed?: string
    sequence?: number
    byte_offset?: number
    byte_length?: number
    is_final?: boolean
    duration_seconds?: number
    audio_base64?: string
    mime_type?: string
    sample_rate?: number
    channels?: number
    audio_chunks?: number
    total_bytes?: number
    track_id?: string
    track_ids?: string[]
    tracks?: Track[]
    track?: Partial<Track>
    status?: "completed" | "cancelled"
    completed?: number
    failed?: number
    cancelled?: number
    error?: {
        code: string
        message: string
    }
    [key: string]: unknown
}

export type LyricsMode = "description" | "written"
export type VocalGender = "female" | "male"

export type LyricsChatRole = "user" | "assistant"

export interface LyricsChatMessage {
    role: LyricsChatRole
    content: string
}

export interface GenerateLyricsParameters {
    messages: LyricsChatMessage[]
    musicDescription: string
    currentLyrics: string
    vocalGender?: VocalGender
}

export type LyricsChatStreamEvent =
    | { type: "delta"; content: string }
    | { type: "done" }
    | { type: "error"; message: string }

export const MAX_PROMPT_LENGTH = 500
export const MAX_LYRICS_LENGTH = 5000
export const MAX_LYRICS_CHAT_MESSAGES = 20
